import {
  Form,
  Input,
  Button,
  Card,
  Space,
  DatePicker,
  Table,
  Divider,
  Modal,
} from 'antd'
import moment from 'moment'
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons'
import useFormAndRoute from '../../@hooks/useFormAndRoute'
import RoleService from '../../@services/role'
import { useCallback, useEffect, useState } from 'react'
import useCreateRoleDialog from './hooks/useCreateRoleDialog'
import useModifierRoleDialog from './hooks/useModifierRoleDialog'
import useDetailRoleDialog from './hooks/useDetailRoleDialog'
import AuthComponent, { isAuth } from '../../components/AuthComponent'

const DefaultFormData = {
  name: '',
  contact: '',
  dateRange: [],
}

const ButtonLinkStyle = { padding: 0, color: '#1890FF' }

export default function RoleList() {
  const onFormDataChanged = useCallback(FormData => {
    console.log('onFormDataChanged', FormData)
  }, [])

  const { form } = useFormAndRoute(DefaultFormData, onFormDataChanged)

  const [pager, setPager] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: total => `共${total}条数据`,
  })

  const onReset = () => {
    form.resetFields()
    // form.submit()
  }

  const openCreateRoleDialog = useCreateRoleDialog(() => {
    search()
  })

  const openModifierRoleDialog = useModifierRoleDialog(() => {
    search()
  })

  const deleteRole = async (role) => {
    Modal.confirm({
      title: '是否继续',
      content: `是否确认删除${role.name}`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        await RoleService.delete(role.id)
        search()
      },
    })
  }

  const openDetailRoleDialog = useDetailRoleDialog()

  const [tableProps, setTableProps] = useState({
    loading: false,
    dataSource: [],
    rowKey: 'id',
    columns: [],
  })

  const hasTableOperation = isAuth([
    '/auth/role/update',
    '/auth/role/delete/{id}',
    '/auth/role/info/{id}',
  ])

  useEffect(() => {
    const columns = [
      {
        title: '角色名称',
        dataIndex: 'name',
        width: 200,
      },
      {
        title: '创建日期',
        width: 180,
        dataIndex: 'createdAt',
        render: date =>
          date ? moment(date).format('YYYY-MM-DD hh:mm:ss') : '',
      },
      {
        title: '备注',
        dataIndex: 'description',
      },
    ]

    if (hasTableOperation) {
      columns.push({
        title: '操作',
        width: 180,
        render: row => {
          const btns = []
          if (isAuth('/auth/role/update')) {
            btns.push(
              <Button
                type="link"
                key={'edit'}
                style={ButtonLinkStyle}
                onClick={() => openModifierRoleDialog(row.id)}
              >
                编辑
              </Button>
            )
          }

          if (isAuth('/auth/role/info/{id}')) {
            btns.push(
              <Button
                type="link"
                key={'detail'}
                style={ButtonLinkStyle}
                onClick={() => openDetailRoleDialog(row.id)}
              >
                查看
              </Button>
            )
          }

          if (isAuth('/auth/role/delete/{id}')) {
            btns.push(
              <Button
                type="link"
                key={'delete'}
                style={ButtonLinkStyle}
                onClick={() => deleteRole(row)}
              >
                删除
              </Button>
            )
          }
          return btns.reduce((pre, item, index) => {
            if (index !== 0) {
              pre.push(<Divider type="vertical" key={`Divider-${index}`} />)
            }
            pre.push(item)
            return pre
          }, [])
        },
      })
    }
    setTableProps(state => ({ ...state, columns }))
  }, [hasTableOperation])

  const [searchParams, setSearchParams] = useState({})

  const search = () => {
    const req = form.getFieldsValue() || {}
    req.startTime = undefined
    req.endTime = undefined
    if (Array.isArray(req.dateRange) && req.dateRange.length === 2) {
      req.startTime = moment(req.dateRange[0].toISOString())
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss')
      req.endTime = moment(req.dateRange[1].toISOString())
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss')
    }

    if (req.dateRange) {
      delete req.dateRange
    }
    setSearchParams(req)
    setPager(state => ({ ...state, page: 1 }))
  }

  useEffect(() => {
    const getData = async () => {
      try {
        setTableProps(state => ({ ...state, loading: true }))
        const res =
          (await RoleService.getRoleList({
            ...searchParams,
            pageIndex: pager.page,
            pageSize: pager.pageSize,
          })) || {}

        setPager(state => ({ ...state, total: res.recordCount || 0 }))

        setTableProps(state => ({
          ...state,
          loading: false,
          dataSource: res.result || [],
        }))
      } catch (error) {
        console.log({ error })
      } finally {
        setTableProps(state => ({ ...state, loading: false }))
      }
    }
    getData()
  }, [pager.pageSize, pager.page, searchParams])

  const onChange = page => {
    setPager(state => ({
      ...state,
      page: page.current,
      pageSize: page.pageSize,
    }))
  }
  return (
    <Space direction="vertical" style={{ width: '100%', textAlign: 'left' }}>
      <Card>
        <Form form={form} layout="inline" onFinish={search} onReset={onReset}>
          <Form.Item name="name" label="角色名称">
            <Input style={{ width: 150 }} placeholder="请输入内容" />
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <DatePicker.RangePicker></DatePicker.RangePicker>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              ghost
              type="primary"
            >
              搜索
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="button"
              icon={<ReloadOutlined />}
              onClick={onReset}
              ghost
              type="primary"
            >
              重置
            </Button>
          </Form.Item>
          <AuthComponent authKey={'/auth/role/create'}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="button"
                icon={<PlusOutlined />}
                onClick={openCreateRoleDialog}
              >
                添加
              </Button>
            </Form.Item>
          </AuthComponent>
        </Form>
      </Card>
      <Card>
        <Table {...tableProps} pagination={pager} onChange={onChange} />
      </Card>
    </Space>
  )
}
